/**
 * Twilio Service
 *
 * API client for Twilio phone system operations.
 * Uses the /api/v1/twilio endpoints (JWT authenticated).
 */
import { api } from './api'

export interface TwilioToken {
  token: string
  identity: string
  expires_in: number
}

export interface CallLogEntry {
  id: string
  call_sid: string
  direction: 'inbound' | 'outbound'
  from_number: string
  to_number: string
  status: string
  duration_seconds: number | null
  answered_by: string | null
  created_at: string
}

export interface SMSConversation {
  phone_number: string
  last_message: string
  last_at: string
  unread_count: number
  message_count: number
}

export interface SMSMessage {
  id: string
  direction: 'inbound' | 'outbound'
  from_number: string
  to_number: string
  body: string
  created_at: string
  is_read: boolean
}

export interface CallerLookup {
  found: boolean
  client_id?: string
  client_name?: string
  contact_name?: string
  contact_title?: string
  client_phone?: string
  recent_calls?: Array<{ direction: string; status: string; duration: number | null; time: string }>
  last_sms?: { direction: string; body: string; time: string } | null
}

export interface Voicemail {
  id: string
  call_sid: string
  recording_sid: string
  recording_url: string
  from_number: string
  duration_seconds: number | null
  transcription: string | null
  is_read: boolean
  created_at: string
}

export const twilioService = {
  async getToken(): Promise<TwilioToken> {
    const response = await api.post('/v1/twilio/token')
    return response.data
  },

  async getCallHistory(limit = 50): Promise<CallLogEntry[]> {
    const response = await api.get('/v1/twilio/history/calls', { params: { limit } })
    return response.data
  },

  async getSMSConversations(): Promise<SMSConversation[]> {
    const response = await api.get('/v1/twilio/sms/conversations')
    return response.data.conversations
  },

  async getSMSThread(phoneNumber: string): Promise<SMSMessage[]> {
    const response = await api.get(`/v1/twilio/sms/conversations/${encodeURIComponent(phoneNumber)}`)
    return response.data.messages
  },

  async sendSMS(to: string, body: string): Promise<{ message_sid: string }> {
    const response = await api.post('/v1/twilio/sms/send', { to, body })
    return response.data
  },

  async getVoicemails(): Promise<Voicemail[]> {
    const response = await api.get('/v1/twilio/voicemails')
    return response.data.voicemails
  },

  async callerLookup(phone: string): Promise<CallerLookup> {
    const response = await api.get('/v1/twilio/caller-lookup', { params: { phone } })
    return response.data
  },

  async hangupCall(callSid: string): Promise<{ success: boolean }> {
    const response = await api.post('/v1/twilio/voice/hangup', { call_sid: callSid })
    return response.data
  },

  async getCallDetail(callId: string): Promise<any> {
    const response = await api.get(`/v1/twilio/calls/${callId}`)
    return response.data
  },

  async addCallNote(callId: string, content: string): Promise<any> {
    const response = await api.post(`/v1/twilio/calls/${callId}/notes`, { content })
    return response.data
  },

  async annotateCall(callId: string, qualityScore: number, qualityIssues: string[]): Promise<any> {
    const response = await api.post(`/v1/twilio/calls/${callId}/annotate`, {
      quality_score: qualityScore,
      quality_issues: qualityIssues,
    })
    return response.data
  },

  async voeVoicemailDrop(callSid: string): Promise<{ success: boolean }> {
    const response = await api.post('/v1/twilio/voe-voicemail-drop', { call_sid: callSid })
    return response.data
  },

  /**
   * Initiate a desk-phone bridge call.
   *
   * `context` is the workflow linkage (P1.5): WHO the call is about. Outbound
   * calls to a previous employer or an unlisted number can never be linked by
   * phone-to-client matching, so the caller states it and the backend stashes
   * it for voice_status to write onto the call log.
   *
   * Both ids MUST be PG uuids as strings — TwilioCallLog.client_id holds
   * str(clients.id). Never pass a Zoho crm id (client_crm_id / crm_account_id):
   * it would carry a second, unmatchable convention. The server is the
   * enforcement point — DeskPhoneCallRequest types both ids as UUID with
   * extra="forbid", so a malformed id is a 422 at the edge, never a silent
   * NULL and never a poisoned column. Do not "helpfully" strip bad ids
   * client-side: that would hide them from the guard that exists to reject
   * them.
   */
  async deskPhoneCall(
    phoneNumber: string,
    sipUser: string = 'kathy',
    context?: { clientId?: string | null; employeeId?: string | null },
  ): Promise<{ call_sid: string; status: string }> {
    const response = await api.post('/v1/twilio/desk-phone/call', {
      phone_number: phoneNumber,
      sip_user: sipUser,
      // Omitted (not null) when absent so the backend keeps its defaults and
      // falls back to number matching.
      ...(context?.clientId ? { client_id: context.clientId } : {}),
      ...(context?.employeeId ? { employee_id: context.employeeId } : {}),
    })
    return response.data
  },

  async deskPhoneHangup(callSid: string): Promise<{ success: boolean }> {
    const response = await api.post('/v1/twilio/desk-phone/hangup', { call_sid: callSid })
    return response.data
  },

  async deskPhoneStatus(callSid: string): Promise<{
    call_sid: string
    status: string
    duration: string | null
    start_time: string | null
    end_time: string | null
  }> {
    const response = await api.get('/v1/twilio/desk-phone/status', { params: { call_sid: callSid } })
    return response.data
  },
}
